import { emptySplitApi as api } from "./baseApi"
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    billingListCurrentPlans: build.query<
      BillingListCurrentPlansApiResponse,
      BillingListCurrentPlansApiArg
    >({
      query: () => ({ url: `/api/billing/plan` }),
    }),
    billingGetRecommendedPlan: build.query<
      BillingGetRecommendedPlanApiResponse,
      BillingGetRecommendedPlanApiArg
    >({
      query: (queryArg) => ({
        url: `/api/billing/plan/recommend`,
        params: {
          monthlySessions: queryArg.monthlySessions,
          offering: queryArg.offering,
          frequency: queryArg.frequency,
        },
      }),
    }),
    billingV2ListCurrentPlans: build.query<
      BillingV2ListCurrentPlansApiResponse,
      BillingV2ListCurrentPlansApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v2/billing/plan`,
        params: {
          industry: queryArg.industry,
        },
      }),
    }),
    billingV2GetRecommendedPlans: build.query<
      BillingV2GetRecommendedPlansApiResponse,
      BillingV2GetRecommendedPlansApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v2/billing/plan/recommend`,
        params: {
          monthlySessions: queryArg.monthlySessions,
          currency: queryArg.currency,
          industry: queryArg.industry,
        },
      }),
    }),
  }),
  overrideExisting: false,
})
export { injectedRtkApi as appApi }
export type BillingListCurrentPlansApiResponse =
  /** status 200  */ ApiResponseDtoOfListResultDtoOfAppBillingPlan
export type BillingListCurrentPlansApiArg = void
export type BillingGetRecommendedPlanApiResponse =
  /** status 200 Will be null if there is no recommended plan */ ApiResponseDtoOfAppBillingPlanWithPrice
export type BillingGetRecommendedPlanApiArg = {
  monthlySessions?: number
  offering?: AppBillingPlanOfferingType
  frequency?: AppBillingPlanPaymentFrequency
}
export type BillingV2ListCurrentPlansApiResponse =
  /** status 200  */ ApiResponseDtoOfListCurrentPlansResult
export type BillingV2ListCurrentPlansApiArg = {
  industry?: string
}
export type BillingV2GetRecommendedPlansApiResponse =
  /** status 200 Will be null if there is no recommended plan */ ApiResponseDtoOfAppBillingQuote
export type BillingV2GetRecommendedPlansApiArg = {
  monthlySessions?: number[]
  currency?: AppBillingPlanCurrency | null
  industry?: string
}
export type AppBillingPlanIndustry = "standard" | "ticketing"
export type AppBillingPlanIndustryInfo = {
  industry?: AppBillingPlanIndustry
  key?: string
  description?: string
}
export type AppBillingPlanCurrency =
  | "undefined"
  | "aud"
  | "usd"
  | "eur"
  | "gbp"
  | "sgd"
export type AppBillingPlanPaymentFrequency = "monthly" | "yearly" | "undefined"
export type AppBillingPlanModelVersion = "undefined" | "version1" | "version2"
export type AppBillingPlanOfferingType =
  | "undefined"
  | "serverAndBrowser"
  | "browserOnly"
export type CurrencyAmount = {
  amount?: number
  /** ISO 4217 currency code */
  currencyCode?: string
  /** Currency symbol */
  currencySymbol?: string
}
export type AppBillingPlanPricingTierMode =
  | "undefined"
  | "volumeTotal"
  | "graduated"
export type AppBillingPlanPricingTierCost = {
  rangeStart?: number
  rangeEnd?: number | null
  cost?: CurrencyAmount
}
export type AppBillingPlanPricing = {
  billingPlanPricingIdentifier?: string
  /** Will be null if there is no active plan */
  billingPlatformSubscriptionItemIdentifier?: string
  lookupKey?: string
  frequency?: AppBillingPlanPaymentFrequency
  baseCost?: CurrencyAmount
  isUsageMetered?: boolean
  hasNoBaseSessionsIncluded?: boolean
  /** Cost per additional session. Null value indicates no charge for additional sessions.
                Null also indicates a tiered pricing model.
                 */
  costPerAdditionalSession?: CurrencyAmount
  tierMode?: AppBillingPlanPricingTierMode | null
  /** Cost per additional session.
   */
  costPerAdditionalSessionTiers?: AppBillingPlanPricingTierCost[]
}
export type AppBillingPlanEntitlements = {
  /** Allowed monthly sessions. Null value indicates unlimited sessions. */
  monthlySessions?: number | null
}
export type AppBillingPlanFeature = {
  isGranted?: boolean
  featureKey?: string
  description?: string
}
export type AppBillingPlan = {
  description?: string
  billingPlanIdentifier?: string
  isCustomPlan?: boolean
  isPayAsYouGo?: boolean
  industry?: AppBillingPlanIndustryInfo
  currency?: AppBillingPlanCurrency
  frequency?: AppBillingPlanPaymentFrequency
  modelVersion?: AppBillingPlanModelVersion
  offering?: AppBillingPlanOfferingType
  pricing?: AppBillingPlanPricing[]
  entitlements?: AppBillingPlanEntitlements
  features?: AppBillingPlanFeature[]
}
export type ListResultDtoOfAppBillingPlan = {
  items?: AppBillingPlan[]
  totalCount?: number
}
export type ApiResponseDtoOfListResultDtoOfAppBillingPlan = {
  result?: ListResultDtoOfAppBillingPlan
}
export type ApiResponseDtoOfObject = {
  result?: any
}
export type ResponseResultType = "undefined" | "success" | "failed"
export type HelpAction = {
  helpActionUrl?: string
  helpActionTitle?: string
}
export type PropertyValidationError = {
  errorKey?: string
  helpActions?: HelpAction[]
  propertyName?: string
  attemptedValue?: any
  errorMessage?: string
  children?: PropertyValidationError[]
}
export type ValidationSummary = {
  properties?: PropertyValidationError[]
}
export type ErrorInfo = {
  message?: string
  code?: number
  correlationId?: string
  validation?: ValidationSummary
}
export type DetailedApiResponseDtoOfObject = ApiResponseDtoOfObject & {
  type?: ResponseResultType
  error?: ErrorInfo
  success?: boolean
}
export type ApiResponseErrorDtoOfObject = DetailedApiResponseDtoOfObject &
  object
export type ApiResponseError = ApiResponseErrorDtoOfObject & object
export type CurrencyAmountTax = {
  /** GST, VAT etc */
  name?: string
  amount?: CurrencyAmount
}
export type CurrencyAmountWithTaxes = CurrencyAmount & {
  taxes?: CurrencyAmountTax[]
  amountExcludingTax?: CurrencyAmount
  totalTax?: CurrencyAmount
}
export type AppBillingPlanPricingCalculation = {
  overageSessionCount?: number | null
  sessionUsagePercent?: number | null
  /** Base plan cost */
  baseCost?: CurrencyAmountWithTaxes
  /** Can be null if no session overage cost */
  sessionOverageCost?: CurrencyAmountWithTaxes
  /** Total cost including base and overage fees */
  totalCost?: CurrencyAmountWithTaxes
}
export type AppBillingPlanWithPrice = {
  plan?: AppBillingPlan
  price?: AppBillingPlanPricingCalculation
}
export type ApiResponseDtoOfAppBillingPlanWithPrice = {
  result?: AppBillingPlanWithPrice
}
export type ListCurrentPlansResult = {
  plans?: {
    [key: string]: AppBillingPlan[]
  }
  industry?: AppBillingPlanIndustryInfo
}
export type ApiResponseDtoOfListCurrentPlansResult = {
  result?: ListCurrentPlansResult
}
export type AppBillingQuoteBreakdown = {
  months?: {
    [key: string]: AppBillingPlanWithPrice[]
  }
  totalPayAsYouGoCost?: CurrencyAmountWithTaxes
  totalStandardPlanCost?: CurrencyAmountWithTaxes
}
export type AppBillingQuote = {
  currency?: AppBillingPlanCurrency
  payYearly?: AppBillingQuoteBreakdown
  payMonthly?: AppBillingQuoteBreakdown
}
export type ApiResponseDtoOfAppBillingQuote = {
  result?: AppBillingQuote
}
export const {
  useBillingListCurrentPlansQuery,
  useBillingGetRecommendedPlanQuery,
  useBillingV2ListCurrentPlansQuery,
  useBillingV2GetRecommendedPlansQuery,
} = injectedRtkApi
